/**
 * Shared checklist templates (saved in DB): all users see the same list.
 * POST still attributes the saver via user_id for per-user save limits.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export type ChecklistTemplateItemKind = 'checkbox' | 'section' | 'subsection';

export interface ProjectChecklistTemplateItem {
  label: string;
  item_kind: ChecklistTemplateItemKind;
  sort_order: number;
}

export interface ProjectChecklistTemplate {
  id: number;
  name: string;
  title: string;
  /** All line labels in order (legacy + display). Prefer `templateItems` for structure. */
  itemLabels: string[];
  /** When present, includes section / subsection / checkbox rows in order. */
  templateItems: ProjectChecklistTemplateItem[];
}

function normItemKind(v: unknown): ChecklistTemplateItemKind {
  const s = typeof v === 'string' ? v.trim() : '';
  if (s === 'section' || s === 'subsection') return s;
  return 'checkbox';
}

export async function fetchChecklistTemplates(userId: number): Promise<ProjectChecklistTemplate[]> {
  const res = await fetch(
    `${API_BASE}/api/checklist-templates?userId=${encodeURIComponent(userId)}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to load checklist templates');
  const raw = (data as { templates?: unknown }).templates;
  if (!Array.isArray(raw)) return [];
  const out: ProjectChecklistTemplate[] = [];
  for (const row of raw) {
    if (row == null || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const id = typeof o.id === 'number' && Number.isInteger(o.id) ? o.id : Number(o.id);
    if (!Number.isInteger(id) || id < 1) continue;
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    const title = typeof o.title === 'string' ? o.title.trim() : '';
    const il = o.item_labels;
    const itemLabels = Array.isArray(il)
      ? il.filter((x): x is string => typeof x === 'string').map((s) => s.trim())
      : [];
    const ti = o.template_items;
    let templateItems: ProjectChecklistTemplateItem[] = [];
    if (Array.isArray(ti) && ti.length > 0) {
      let order = 0;
      for (const entry of ti) {
        if (entry == null || typeof entry !== 'object') continue;
        const r = entry as Record<string, unknown>;
        const label = typeof r.label === 'string' ? r.label.trim() : '';
        if (!label) continue;
        const item_kind = normItemKind(r.item_kind);
        const sort_order =
          typeof r.sort_order === 'number' && Number.isInteger(r.sort_order) ? r.sort_order : order;
        templateItems.push({ label, item_kind, sort_order });
        order += 1;
      }
    }
    if (templateItems.length === 0 && itemLabels.length > 0) {
      templateItems = itemLabels.map((label, i) => ({ label, item_kind: 'checkbox' as const, sort_order: i }));
    }
    if (!name) continue;
    out.push({
      id,
      name,
      title: title || 'Checklist',
      itemLabels: itemLabels.length > 0 ? itemLabels : templateItems.map((x) => x.label),
      templateItems,
    });
  }
  return out;
}

export async function createChecklistTemplate(payload: {
  user_id: number;
  name: string;
  title: string;
  item_labels?: string[];
  template_items?: { label: string; item_kind: ChecklistTemplateItemKind }[];
}): Promise<ProjectChecklistTemplate> {
  const res = await fetch(`${API_BASE}/api/checklist-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to save checklist template');
  const o = data as Record<string, unknown>;
  const id = typeof o.id === 'number' && Number.isInteger(o.id) ? o.id : Number(o.id);
  const name = typeof o.name === 'string' ? o.name : '';
  const title = typeof o.title === 'string' ? o.title : 'Checklist';
  const il = o.item_labels;
  const itemLabels = Array.isArray(il)
    ? il.filter((x): x is string => typeof x === 'string').map((s) => s.trim())
    : [];
  const ti = o.template_items;
  let templateItems: ProjectChecklistTemplateItem[] = [];
  if (Array.isArray(ti)) {
    let order = 0;
    for (const entry of ti) {
      if (entry == null || typeof entry !== 'object') continue;
      const r = entry as Record<string, unknown>;
      const label = typeof r.label === 'string' ? r.label.trim() : '';
      if (!label) continue;
      templateItems.push({ label, item_kind: normItemKind(r.item_kind), sort_order: order });
      order += 1;
    }
  }
  if (templateItems.length === 0) {
    templateItems = itemLabels.map((label, i) => ({ label, item_kind: 'checkbox' as const, sort_order: i }));
  }
  return { id, name, title: title || 'Checklist', itemLabels, templateItems };
}
